import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Target, BarChart3, TrendingUp, CheckSquare, Megaphone, Calendar, Users, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EOSDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get(`${API}/eos/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching EOS dashboard stats:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground dark:text-slate-400">Loading EOS Dashboard...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Target className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            <div>
              <h1 className="text-3xl font-bold dark:text-white">EOS Hub</h1>
              <p className="text-sm text-muted-foreground dark:text-slate-400">
                Entrepreneurial Operating System - Vision, Traction, and Healthy Team
              </p>
            </div>
          </div>
        </div>

        {/* Quick Stats Cards with Blue Glow */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Next L10 Meeting */}
          <Card 
            className="cursor-pointer hover:shadow-lg transition-all dark:bg-slate-800/60 border-2 border-blue-500 dark:border-blue-400 hover:border-blue-400" 
            onClick={() => navigate('/eos/l10-meetings')}
            style={{
              boxShadow: '0 0 20px rgba(59, 130, 246, 0.3), 0 0 40px rgba(59, 130, 246, 0.1)'
            }}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                {stats?.next_meeting && (
                  <Badge variant="outline" className="text-xs dark:border-slate-600 dark:text-slate-300">
                    Scheduled
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground dark:text-slate-400">Next L10</p>
                {stats?.next_meeting ? (
                  <>
                    <p className="text-lg font-bold dark:text-white">{stats.next_meeting.title}</p>
                    <p className="text-xs text-muted-foreground dark:text-slate-500">
                      {new Date(stats.next_meeting.scheduled_date).toLocaleDateString()} at{' '}
                      {new Date(stats.next_meeting.scheduled_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                    <Button size="sm" className="w-full mt-2">
                      View Meeting
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="text-lg font-bold dark:text-white">No meetings scheduled</p>
                    <Button size="sm" variant="outline" className="w-full mt-2 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700" onClick={(e) => { e.stopPropagation(); navigate('/eos/l10-meetings'); }}>
                      Schedule L10
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Scorecard */}
          <Card 
            className="cursor-pointer hover:shadow-lg transition-all dark:bg-slate-800/60 border-2 border-blue-500 dark:border-blue-400 hover:border-blue-400" 
            onClick={() => navigate('/eos/scorecard')}
            style={{
              boxShadow: '0 0 20px rgba(59, 130, 246, 0.3), 0 0 40px rgba(59, 130, 246, 0.1)'
            }}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <BarChart3 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                <Badge variant={stats?.scorecard?.status === 'on_track' ? 'default' : 'destructive'} className="dark:bg-emerald-600">
                  {stats?.scorecard?.green_count || 0}/{stats?.scorecard?.total_metrics || 0} Green
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground dark:text-slate-400">Scorecard</p>
                <p className="text-2xl font-bold dark:text-white">
                  {stats?.scorecard?.status === 'on_track' ? '🟢 On Track' : '🟡 Needs Attention'}
                </p>
                <p className="text-xs text-muted-foreground dark:text-slate-500">Weekly metrics</p>
                <Button size="sm" variant="outline" className="w-full mt-2 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700">
                  View Metrics
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* My Rocks */}
          <Card 
            className="cursor-pointer hover:shadow-lg transition-all dark:bg-slate-800/60 border-2 border-blue-500 dark:border-blue-400 hover:border-blue-400" 
            onClick={() => navigate('/eos/rocks')}
            style={{
              boxShadow: '0 0 20px rgba(59, 130, 246, 0.3), 0 0 40px rgba(59, 130, 246, 0.1)'
            }}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <Target className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                <Badge variant="outline" className="dark:border-slate-600 dark:text-slate-300">
                  {stats?.rocks?.on_track || 0}/{stats?.rocks?.total || 0} On Track
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground dark:text-slate-400">My Rocks</p>
                <p className="text-2xl font-bold dark:text-white">{stats?.rocks?.total || 0} Rocks</p>
                <p className="text-xs text-muted-foreground dark:text-slate-500">Quarterly goals</p>
                <Button size="sm" variant="outline" className="w-full mt-2 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700">
                  View All Rocks
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Open Issues */}
          <Card 
            className="cursor-pointer hover:shadow-lg transition-all dark:bg-slate-800/60 border-2 border-blue-500 dark:border-blue-400 hover:border-blue-400" 
            onClick={() => navigate('/eos/issues')}
            style={{
              boxShadow: '0 0 20px rgba(59, 130, 246, 0.3), 0 0 40px rgba(59, 130, 246, 0.1)'
            }}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                {stats?.issues?.open_count > 0 && (
                  <Badge variant="destructive">{stats.issues.open_count} Open</Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground dark:text-slate-400">Open Issues</p>
                <p className="text-2xl font-bold dark:text-white">{stats?.issues?.open_count || 0}</p>
                <p className="text-xs text-muted-foreground dark:text-slate-500">Needs attention</p>
                <Button size="sm" variant="outline" className="w-full mt-2 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700">
                  View Issues
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* My To-Dos Section */}
        <Card className="dark:bg-slate-800/60 dark:border-slate-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckSquare className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <CardTitle className="dark:text-white">My To-Dos</CardTitle>
              </div>
              <Button size="sm" variant="outline" className="dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700" onClick={() => navigate('/eos/todos')}>
                View All
              </Button>
            </div>
            <CardDescription className="dark:text-slate-400">
              {stats?.todos?.due_this_week > 0 && (
                <span className="text-orange-600 dark:text-orange-400 font-medium">
                  {stats.todos.due_this_week} due this week
                </span>
              )}
              {stats?.todos?.due_this_week === 0 && "No to-dos due this week"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {stats?.todos?.count > 0 ? (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground dark:text-slate-400">
                  You have {stats.todos.count} open to-do{stats.todos.count !== 1 ? 's' : ''}
                </p>
                <Button onClick={() => navigate('/eos/todos')} className="w-full">
                  Manage To-Dos
                </Button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground dark:text-slate-400 text-center py-4">
                No open to-dos. Great job! 🎉
              </p>
            )}
          </CardContent>
        </Card>

        {/* Recent Headlines */}
        <Card className="dark:bg-slate-800/60 dark:border-slate-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Megaphone className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                <CardTitle className="dark:text-white">Recent Headlines</CardTitle>
              </div>
              <Button size="sm" variant="outline" className="dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700" onClick={() => navigate('/eos/headlines')}>
                View All
              </Button>
            </div>
            <CardDescription className="dark:text-slate-400">Good news from the team</CardDescription>
          </CardHeader>
          <CardContent>
            {stats?.headlines && stats.headlines.length > 0 ? (
              <div className="space-y-3">
                {stats.headlines.map((headline, index) => (
                  <div key={index} className="p-3 rounded-lg bg-muted/50 dark:bg-slate-700/50 hover:bg-muted dark:hover:bg-slate-700 transition-colors">
                    <p className="text-sm font-medium dark:text-white">{headline.message}</p>
                    <p className="text-xs text-muted-foreground dark:text-slate-500 mt-1">
                      {new Date(headline.created_at).toLocaleDateString()} - {headline.category}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-sm text-muted-foreground dark:text-slate-400 mb-3">No headlines yet</p>
                <Button size="sm" variant="outline" className="dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700" onClick={() => navigate('/eos/headlines')}>
                  Share Good News
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* EOS Modules Grid */}
        <Card className="dark:bg-slate-800/60 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="dark:text-white">EOS Modules</CardTitle>
            <CardDescription className="dark:text-slate-400">Access all EOS tools and features</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Button variant="outline" onClick={() => navigate('/eos/l10-meetings')} className="h-24 flex flex-col items-center justify-center gap-2 dark:border-slate-600 dark:hover:bg-slate-700">
                <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                <div className="text-center">
                  <div className="font-semibold text-sm dark:text-white">L10 Meetings</div>
                  <div className="text-xs text-muted-foreground dark:text-slate-500">Weekly leadership</div>
                </div>
              </Button>
              <Button variant="outline" onClick={() => navigate('/eos/scorecard')} className="h-24 flex flex-col items-center justify-center gap-2 dark:border-slate-600 dark:hover:bg-slate-700">
                <BarChart3 className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                <div className="text-center">
                  <div className="font-semibold text-sm dark:text-white">Scorecard</div>
                  <div className="text-xs text-muted-foreground dark:text-slate-500">Weekly metrics</div>
                </div>
              </Button>
              <Button variant="outline" onClick={() => navigate('/eos/rocks')} className="h-24 flex flex-col items-center justify-center gap-2 dark:border-slate-600 dark:hover:bg-slate-700">
                <Target className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                <div className="text-center">
                  <div className="font-semibold text-sm dark:text-white">Rocks</div>
                  <div className="text-xs text-muted-foreground dark:text-slate-500">90-day priorities</div>
                </div>
              </Button>
              <Button variant="outline" onClick={() => navigate('/eos/issues')} className="h-24 flex flex-col items-center justify-center gap-2 dark:border-slate-600 dark:hover:bg-slate-700">
                <TrendingUp className="h-6 w-6 text-red-600 dark:text-red-400" />
                <div className="text-center">
                  <div className="font-semibold text-sm dark:text-white">Issues (IDS)</div>
                  <div className="text-xs text-muted-foreground dark:text-slate-500">Identify, discuss, solve</div>
                </div>
              </Button>
              <Button variant="outline" onClick={() => navigate('/eos/todos')} className="h-24 flex flex-col items-center justify-center gap-2 dark:border-slate-600 dark:hover:bg-slate-700">
                <CheckSquare className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                <div className="text-center">
                  <div className="font-semibold text-sm dark:text-white">To-Dos</div>
                  <div className="text-xs text-muted-foreground dark:text-slate-500">Action items</div>
                </div>
              </Button>
              <Button variant="outline" onClick={() => navigate('/eos/headlines')} className="h-24 flex flex-col items-center justify-center gap-2 dark:border-slate-600 dark:hover:bg-slate-700">
                <Megaphone className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                <div className="text-center">
                  <div className="font-semibold text-sm dark:text-white">Headlines</div>
                  <div className="text-xs text-muted-foreground dark:text-slate-500">Good news</div>
                </div>
              </Button>
              <Button variant="outline" onClick={() => navigate('/eos/accountability-chart')} className="h-24 flex flex-col items-center justify-center gap-2 dark:border-slate-600 dark:hover:bg-slate-700">
                <Users className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                <div className="text-center">
                  <div className="font-semibold text-sm dark:text-white">Accountability</div>
                  <div className="text-xs text-muted-foreground dark:text-slate-500">Org chart</div>
                </div>
              </Button>
              <Button variant="outline" onClick={() => navigate('/eos/vto')} className="h-24 flex flex-col items-center justify-center gap-2 dark:border-slate-600 dark:hover:bg-slate-700">
                <Target className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                <div className="text-center">
                  <div className="font-semibold text-sm dark:text-white">V/TO</div>
                  <div className="text-xs text-muted-foreground dark:text-slate-500">Vision & traction</div>
                </div>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default EOSDashboard;
